import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useLang } from "../lib/lang";
import { useAuth } from "../lib/auth";
import { useSeo } from "../lib/seo";
import { api } from "../lib/api";

export default function CheckoutSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [order, setOrder] = useState(null);
  const polled = useRef(0);
  useSeo({ title: "Order confirmed — Brand and Behavior", noindex: true });

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return undefined; }
    let alive = true;
    const tick = async () => {
      polled.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (!alive) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          setOrder(data.order);
          return;
        }
        if (polled.current >= 12) { setStatus("timeout"); return; }
        setTimeout(tick, 2500);
      } catch (err) {
        if (!alive) return;
        console.error("Checkout status polling failed:", err);
        setStatus("error");
      }
    };
    tick();
    return () => { alive = false; };
  }, [sessionId]);

  // Once paid: if logged in → claim order and go to intake; else send to login (will come back)
  useEffect(() => {
    if (status !== "paid" || !order) return;
    if (loading) return;
    (async () => {
      if (user) {
        try {
          await api.post(`/orders/${order.order_id}/claim`);
        } catch (err) {
          console.warn("Order claim failed (may already be claimed):", err);
        }
        navigate(`/intake/${order.order_id}`, { replace: true });
      } else {
        // Save target intake URL and ask user to sign in
        navigate(`/login?next=${encodeURIComponent(`/intake/${order.order_id}`)}`, { replace: true });
      }
    })();
  }, [status, order, user, loading, navigate]);

  return (
    <main className="app-main" data-testid="checkout-success">
      <section className="page-hero">
        <div className="kicker">{t("Checkout", "Checkout")}</div>
        {status === "polling" && (<><h1>{t("Confirming payment", "Confirmando pago")}<em className="it">…</em></h1><p>{t("This usually takes a few seconds.", "Esto suele tardar unos segundos.")}</p></>)}
        {status === "paid" && (<><h1><em className="it">{t("Payment confirmed", "Pago confirmado")}</em></h1><p>{t("Redirecting you to the intake form…", "Te llevamos al cuestionario…")}</p></>)}
        {status === "timeout" && (<><h1>{t("Still confirming…", "Aún confirmando…")}</h1><p>{t("Refresh in a moment. If you were charged, your order will appear in your dashboard.", "Refresca en un momento. Si se te cobró, tu pedido aparecerá en tu panel.")}</p><Link to="/dashboard" className="button">{t("Open dashboard", "Abrir panel")}</Link></>)}
        {status === "error" && (<><h1>{t("Something went wrong", "Algo salió mal")}</h1><p>{t("Contact hello@brandandbehavior.com if you were charged.", "Contacta hello@brandandbehavior.com si se te cobró.")}</p></>)}
      </section>
    </main>
  );
}
